/**
 * The shift write lock (issue #71): one module, one interface, every writer of a shift behind it.
 * Vaktplan (moves, resizes, task and edge moves, publishing, approving, duplicating, deleting,
 * each shift of a bulk action), the editor's save (ShiftDrawer.submit), the panel's writes
 * (Panel busy/onGive/onMoveTask/onBoundary/onLunch) and Oversikt → Dagen's delete and save all
 * cross this one seam, so the race and the unknown rule live in one place (locality) and a
 * fix here is a fix for every call site (leverage).
 *
 * What a caller must know:
 * - one write per shift at a time: a second hold() on a shift still in flight, or whose last
 *   outcome nobody knows, is `busy` and sends nothing (the double-click bug the old page had);
 * - a refusal the server gave (4xx as the client throws it, 499 excepted) is `refused` with the
 *   server's own words, and frees the shift;
 * - anything else that failed (no answer, 5xx, a timeout) is `unknown`: every shift it touched
 *   stays locked until a read of the shifts succeeds (reread), so nothing is written over a
 *   change that may already have happened. A failed read keeps the lock. Reread is the only way
 *   out, because a write may add history (ADR 0021). The shell's own unknownWrites count is kept
 *   by api/client.ts for the same requests and is not touched here;
 * - the pure shiftLock() is the module; useShiftLock() is the adapter that makes a page render
 *   again when the lock changes. Tests use the former with a fake `f` (lock.test.ts).
 */
import { ApiError } from '@/api/client';
import { T } from '@/i18n';
import { toast } from '@/ui';
import { useMemo, useRef, useState } from 'react';

export type Held<V> =
  | { kind: 'done'; value: V }
  | { kind: 'busy' }
  | { kind: 'refused'; message: string }
  | { kind: 'unknown' };

export interface ShiftLock<D> {
  /** Run `f` with these shifts held; see the module comment for what each kind means. */
  hold<V>(ids: number[], f: () => Promise<V>): Promise<Held<V>>;
  /** In flight, or waiting for a read after an unknown outcome. */
  busy(id: number): boolean;
  /** The shifts whose last write has no known outcome; the bar above the plan counts them. */
  readonly unsure: ReadonlySet<number>;
  /** The way out of an unknown outcome: null when the read failed (and the lock stays), else its data. */
  reread(): Promise<D | null>;
  /**
   * hold() plus the toasts every page says the same way: the label (or the note `f` returns,
   * such as a failed notice after a saved change) when done, the server's words when refused,
   * BUSY when busy, UNKNOWN and a reread when unknown. True when done.
   */
  op(ids: number[], label: string, f: () => Promise<string | undefined>): Promise<boolean>;
}

export const UNKNOWN = 'Vi vet ikke om endringen ble lagret. Les på nytt før du prøver igjen.';
export const BUSY = 'Vakten venter på svar på en annen endring. Les på nytt før du prøver igjen.';

/** True for an answer the server gave on purpose: the write was refused, nothing is in doubt. */
export const refused = (e: unknown) => {
  const code = e instanceof ApiError ? e.status : undefined;
  return code != null && code >= 400 && code < 500 && code !== 499;
};

/**
 * @param read reads the shifts; it must reject when the read failed, since only a read that
 *   succeeds unlocks.
 * @param changed called whenever busy() or unsure would answer differently (the hook renders).
 */
export function shiftLock<D>(read: () => Promise<D>, changed: () => void = () => {}): ShiftLock<D> {
  const inflight = new Set<number>();
  const unknown = new Set<number>();
  const lock: ShiftLock<D> = {
    async hold(ids, f) {
      // the sets, not rendered state: a double click lands before the state has rendered
      if (ids.some((id) => inflight.has(id) || unknown.has(id))) return { kind: 'busy' };
      for (const id of ids) inflight.add(id);
      changed();
      try {
        return { kind: 'done', value: await f() };
      } catch (e) {
        if (refused(e)) return { kind: 'refused', message: (e as Error).message };
        for (const id of ids) unknown.add(id);
        return { kind: 'unknown' };
      } finally {
        for (const id of ids) inflight.delete(id);
        changed();
      }
    },
    busy: (id) => inflight.has(id) || unknown.has(id),
    unsure: unknown,
    async reread() {
      let data: D;
      try {
        data = await read();
      } catch {
        return null;
      }
      unknown.clear();
      changed();
      return data;
    },
    async op(ids, label, f) {
      const r = await lock.hold(ids, f);
      if (r.kind === 'done') toast(r.value ?? label);
      else if (r.kind === 'refused') toast(r.message);
      else if (r.kind === 'busy') toast(T(BUSY));
      else {
        toast(T(UNKNOWN));
        await lock.reread();
      }
      return r.kind === 'done';
    },
  };
  return lock;
}

/** The page's lock: one per mount, rendering the page again whenever it changes. */
export function useShiftLock<D>(read: () => Promise<D>): ShiftLock<D> {
  const [, tick] = useState(0);
  const latest = useRef(read);
  latest.current = read;
  // one lock for the page's life; `read` is reached through the ref, so it is not a dependency
  return useMemo(
    () =>
      shiftLock(
        () => latest.current(),
        () => tick((n) => n + 1),
      ),
    [],
  );
}
